import {
    Component,
    ContentChildren,
    EventEmitter,
    HostBinding,
    Input,
    Optional,
    Output,
    QueryList,
    ViewChild
} from "@angular/core";
import {TouchableDirective} from "../../ui-elements/touchable.directive";
import {AbstractControl, NgControl, NgModel} from "@angular/forms";
import {FormComponent} from "../form/form.component";
import {ValidationMessage} from "@fedelta-pos/data-client";
import {FieldValidationMessage} from "./field-validation-message";
import {ValidationErrors} from "@angular/forms/src/directives/validators";

@Component({
    selector: "woa-field",
    templateUrl: "./field.component.html",
})
export class FieldComponent {
    @Input() label: string;
    @Input() link: string;
    @Input() message: string;
    @Input() validationMessage: string;
    @Input() translationParams: any = {};
    @Input() @HostBinding("class.no-message") noMessage: boolean = false;
    @Input() showMessageSpinner: boolean = false;
    @Input() required: boolean;
    @Input() readOnly: boolean = false;
    @Input() disabled: boolean = false;
    @Input() defaultInput: HTMLElement;
    @Input() useContentModels: boolean = false;

    @Output() onActivateField: EventEmitter<void> = new EventEmitter();
    @Output() onActivateLink: EventEmitter<void> = new EventEmitter();

    @ViewChild(TouchableDirective) public touchable: TouchableDirective;
    @ContentChildren(NgModel, {descendants: true}) private models: QueryList<NgModel>;

    constructor(@Optional() private formComponent: FormComponent,
                @Optional() private ngControl: NgControl) {
    }

    get showMessages(): boolean {
        return !this.noMessage && this.showMessageSpinner || !!this.message || !!this.validationMessage || this.hasError;
    }

    get isRequired(): boolean {
        if (this.required) {
            return true;
        }
        if (!this.useContentModels && this.ngControl && this.ngControl.validator) {
            const validator = this.ngControl.validator({} as AbstractControl);
            return validator && validator.required;
        }
        if (this.useContentModels && this.models) {
            return !!this.models.find((model: NgModel) => {
                const validator = model.validator({} as AbstractControl);
                return validator && validator.required;
            });
        }
        return false;
    }

    get hasError(): boolean {
        if (!this.formComponent) {
            return false;
        }
        if (!this.useContentModels && this.ngControl) {
            return this.formComponent.hasError(this.ngControl.control);
        }
        if (this.useContentModels && this.models) {
            return !!this.models.find((model: NgModel) => this.formComponent.hasError(model.control));
        }
        return false;
    }

    get fieldValidationMessages(): FieldValidationMessage[] {
        if (!this.hasError) {
            return [];
        }
        if (!this.useContentModels && this.ngControl) {
            return Object.keys(this.ngControl.control.errors)
                .map((key: string) => this.mapStandardError(this.ngControl.control.errors, key))
                .filter((error: any) => error.message);
        }
        if (this.useContentModels && this.models) {
            let messages: FieldValidationMessage[] = [];
            this.models.filter((model: NgModel) => !!model.control.errors)
                .forEach((model: NgModel) => Object.keys(model.control.errors)
                    .map((key: string) => this.mapStandardError(model.control.errors, key))
                    .filter((message: FieldValidationMessage) => !messages.find((existing: FieldValidationMessage) => message.validationName === existing.validationName))
                    .forEach((message: FieldValidationMessage) => messages.push(message)));
            return messages;
        }
        return [];
    }

    get serverValidationMessages(): ValidationMessage[] {
        return this.formComponent && this.ngControl && this.formComponent.hasError(this.ngControl.control, "server") ? this.ngControl.control.errors.server.validationMessages : undefined;
    }

    /**
     * Handle standard angular validations (convert to FieldValidationMessage)
     */
    private mapStandardError(errors: ValidationErrors, key: string): FieldValidationMessage {
        return key === "required" ? new FieldValidationMessage(key, "woa.validation.common.required", errors[key])
            : key === "minlength" ? new FieldValidationMessage(key, "woa.validation.common.minLength", errors[key])
                : key === "maxlength" ? new FieldValidationMessage(key, "woa.validation.common.maxLength", errors[key])
                    : errors[key];
    }

    activateField(event: Event): void {
        event.stopPropagation();
        if (event.srcElement.tagName !== "INPUT" && this.defaultInput) {
            this.defaultInput.focus();
        }
        this.onActivateField.emit();
    }
}



// WEBPACK FOOTER //
// ./src/app/inputs/field/field.component.ts